import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import MarkdownView from '../components/MarkdownView';
import Loading from '../components/Loading';
import ErrorMessage from '../components/ErrorMessage';
import { API_V1, apiGet } from '../lib/api';
import { openInDoubao } from '../lib/doubao';
import { shareArticle } from '../lib/share';
import { googleZhUrl } from '../lib/links';

const API = `${API_V1}/digests`;

export default function ArticlePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [article, setArticle] = useState(null);
  const [err, setErr] = useState('');
  const [prevNext, setPrevNext] = useState({ prev: null, next: null });
  const [showTop, setShowTop] = useState(false);

  // ids/from 优先读 react-router state（左键 SPA 导航），兜底读 URL query（中键/右键新标签页打开）。
  const params = new URLSearchParams(location.search);
  const ids = location.state?.ids || (params.get('ids') ? params.get('ids').split(',').map(Number) : []);
  const from = location.state?.from || params.get('from') || '';
  const idx = ids.indexOf(Number(id));
  const prevId = idx > 0 ? ids[idx - 1] : null;
  const nextId = idx >= 0 && idx < ids.length - 1 ? ids[idx + 1] : null;

  useEffect(() => {
    setArticle(null);
    setErr('');
    apiGet(`${API}/articles/${id}`)
      .then(setArticle)
      .catch(e => setErr(e.message));
    window.scrollTo(0, 0);
  }, [id]);

  // 拉取上一篇/下一篇的标题
  useEffect(() => {
    if (!prevId && !nextId) {
      setPrevNext({ prev: null, next: null });
      return;
    }
    fetch(`${API}/articles/by-ids`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify([prevId, nextId].filter(Boolean)),
    })
      .then(r => r.json())
      .then(d => {
        const map = Object.fromEntries(d.papers.map(p => [p.id, p]));
        setPrevNext({
          prev: prevId && map[prevId] ? { id: prevId, title: map[prevId].title } : null,
          next: nextId && map[nextId] ? { id: nextId, title: map[nextId].title } : null,
        });
      })
      .catch(() => setPrevNext({ prev: null, next: null }));
  }, [prevId, nextId]);

  // 回顶按钮：滚动超过阈值显示
  useEffect(() => {
    const onScroll = () => setShowTop(window.scrollY > 400);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  if (err) return <ErrorMessage message={err} />;
  if (!article) return <Loading />;

  const arxivUrl = article.arxiv_id ? `https://arxiv.org/abs/${article.arxiv_id}` : '';
  const pdfUrl = article.pdf_url || (article.arxiv_id ? `https://arxiv.org/pdf/${article.arxiv_id}.pdf` : '');

  const goTo = (aid) => navigate(
    `/article/${aid}?from=${encodeURIComponent(from)}${ids.length ? `&ids=${ids.join(',')}` : ''}`,
    { state: { ids, from } },
  );

  const navButtons = (
    <div className="article-pn">
      {prevNext.prev ? (
        <button className="pn-btn" onClick={() => goTo(prevNext.prev.id)} title={prevNext.prev.title}>
          <i className="fas fa-arrow-left" />
          <span className="pn-text">
            <span className="pn-label">上一篇</span>
            <span className="pn-title">{prevNext.prev.title}</span>
          </span>
        </button>
      ) : <span className="pn-btn disabled" />}
      {prevNext.next ? (
        <button className="pn-btn next" onClick={() => goTo(prevNext.next.id)} title={prevNext.next.title}>
          <span className="pn-text right">
            <span className="pn-label">下一篇</span>
            <span className="pn-title">{prevNext.next.title}</span>
          </span>
          <i className="fas fa-arrow-right" />
        </button>
      ) : <span className="pn-btn disabled" />}
    </div>
  );

  return (
    <div className="article-page">
      <div className="article-bar">
        <button className="btn btn-sm btn-ghost" onClick={() => navigate(-1)}>
          <i className="fas fa-arrow-left" /> 返回
        </button>
        <span className="article-bar-title">{article.title}</span>
        <div className="article-actions">
          <button className="btn btn-sm btn-outline" onClick={() => shareArticle(article.id)} title="分享（复制公开链接）">
            <i className="fas fa-share-alt" /> 分享
          </button>
          {article.arxiv_id && (
            <a className="btn btn-sm btn-outline" href={googleZhUrl(article.arxiv_id)} target="_blank" rel="noreferrer" title="谷歌全文翻译（翻译 arXiv HTML 版整页）">
              <i className="fab fa-google" /> 谷歌翻译
            </a>
          )}
          {pdfUrl && (
            <button className="btn btn-sm btn-outline" onClick={() => openInDoubao(pdfUrl)} title="豆包解读（复制提示词并打开豆包，粘贴发送）">
              <img className="doubao-icon" src="/doubao-icon.png" alt="豆包" /> 豆包翻译
            </button>
          )}
          {arxivUrl && (
            <a className="btn btn-sm btn-ghost" href={arxivUrl} target="_blank" rel="noreferrer" title="arXiv 原文">
              <i className="fas fa-external-link-alt" />
            </a>
          )}
        </div>
      </div>

      {(prevNext.prev || prevNext.next) && navButtons}

      <div className="article-meta">
        {article.authors && <span><i className="fas fa-users" /> {article.authors}</span>}
        {article.pub_date && <span><i className="fas fa-calendar" /> {article.pub_date}</span>}
        {article.topic && <span className="tag">{article.topic}</span>}
      </div>

      <MarkdownView content={article.content_md} />

      {(prevNext.prev || prevNext.next) && navButtons}

      {showTop && (
        <div className="float-nav">
          <button
            className="float-nav-btn"
            onClick={() => window.scrollTo(0, 0)}
            title="回到顶部"
          >
            <i className="fas fa-arrow-up" />
          </button>
          <button
            className="float-nav-btn"
            onClick={() => window.scrollTo(0, document.body.scrollHeight)}
            title="回到底部"
          >
            <i className="fas fa-arrow-down" />
          </button>
        </div>
      )}
    </div>
  );
}
